import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';

export const RedemptionPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('redemption.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('redemption.title', 'NFT Redemption Program')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('redemption.description', 'Redeem your NFTs for physical gold through our secure redemption process')}
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" size="lg" className="px-8 py-4 text-lg">
              <i className="fas fa-book mr-2"></i>
              {t('redemption.cta.guide', 'Redemption Guide')}
            </Button>
          </div>
        </div>
      </section>

      {/* Redemption Process */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('redemption.hero.title')}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('redemption.hero.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">1</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-wallet text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step1.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step1.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step1.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step1.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step1.detail3')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step1.detail4')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">2</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-file-alt text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step2.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step2.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step2.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step2.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step2.detail3')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step2.detail4')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">3</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-exchange-alt text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step3.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step3.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step3.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step3.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step3.detail3')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">4</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-coins text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step4.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step4.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step4.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step4.detail2')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">5</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-bell text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step5.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step5.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step5.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step5.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step5.detail3')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step5.detail4')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6 */}
            <div className="modern-card group">
              <div className="flex items-start gap-6">
                <div className="step-number group-hover:scale-110 transition-transform">6</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <i className="fas fa-gift text-2xl text-gold"></i>
                    <h3 className="text-2xl font-bold text-white group-hover:text-gold transition-colors">
                      {t('redemption.flow.step6.title')}
                    </h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {t('redemption.flow.step6.description')}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step6.detail1')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step6.detail2')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step6.detail3')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-400">
                      <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                        <i className="fas fa-check text-gold text-xs"></i>
                      </div>
                      <span>{t('redemption.flow.step6.detail4')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="section relative bg-surface/50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('redemption.requirements.title', 'Requirements & Eligibility')}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('redemption.requirements.description', 'Ensure you meet all requirements for successful redemption')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="modern-card">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-id-card text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t('redemption.requirements.identity.title')}
                </h3>
                <p className="text-gray-400">
                  {t('redemption.requirements.identity.description')}
                </p>
              </div>
            </div>

            <div className="modern-card">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-map-marker-alt text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t('redemption.requirements.location.title')}
                </h3>
                <p className="text-gray-400">
                  {t('redemption.requirements.location.description')}
                </p>
              </div>
            </div>

            <div className="modern-card">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-university text-2xl text-gold"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {t('redemption.requirements.bank.title')}
                </h3>
                <p className="text-gray-400">
                  {t('redemption.requirements.bank.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('redemption.faq.title', 'Frequently Asked Questions')}
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('redemption.faq.description', 'Common questions about the redemption process')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            <div className="modern-card">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('redemption.faq.q1.question')}
              </h3>
              <p className="text-gray-400">
                {t('redemption.faq.q1.answer')}
              </p>
            </div>

            <div className="modern-card">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('redemption.faq.q2.question')}
              </h3>
              <p className="text-gray-400">
                {t('redemption.faq.q2.answer')}
              </p>
            </div>

            <div className="modern-card">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('redemption.faq.q3.question')}
              </h3>
              <p className="text-gray-400">
                {t('redemption.faq.q3.answer')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section relative">
        <div className="container">
          <div className="modern-card p-12 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('redemption.cta.title', 'Ready to Redeem Your NFTs?')}
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              {t('redemption.cta.description', 'Start your redemption journey today and convert your digital assets into physical gold')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="primary" size="lg" className="px-8 py-4 text-lg">
                <i className="fas fa-envelope mr-2"></i>
                {t('redemption.cta.contact', 'Contact Support')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};